import Link from 'next/link';
import { useEffect, useState } from 'react';

import { formatCrossChainDuration } from '@/lib/api/0x-cross-chain/status';

import type { Transaction } from '@/store/transaction/types';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { XIcon } from '@/ui/Icons/XIcon';

import { classNames } from '@/utils/classnames';

import { PendingTradeArt } from './PendingTradeArt';
import { getPendingStatus } from './utils';

const DETAILS_BUTTON_CLASS_NAME = buttons({
  size: 'large',
  color: 'gray',
  shape: 'pill',
  class: 'animate-[fadeIn_500ms_cubic-bezier(0.4,0,0,1)]',
});

/** Renders same-chain and cross-chain progress with caller-owned checkout actions. */
export const PendingTransactionView = ({
  transaction,
  onDismiss,
}: {
  transaction: Transaction;
  onDismiss: () => void;
}) => {
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const secondsInterval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(secondsInterval);
  }, []);

  const elapsedSeconds = Math.max(0, Math.floor((now - Date.parse(transaction.timestamp)) / 1000));
  const { title, body, estimatedSeconds, details } = getPendingStatus(transaction, elapsedSeconds);

  return (
    <div className="relative flex flex-col justify-between gap-6 size-full">
      <button
        type="button"
        aria-label="Dismiss pending screen"
        onClick={onDismiss}
        className="absolute top-4 right-4 z-20 p-2 rounded-full text-token-gray-900 hover:bg-token-gray-200 transition-colors"
      >
        <XIcon className="size-5" />
      </button>

      <div className="pt-6 mx-6">
        <PendingTradeArt sellToken={transaction.sellToken} buyToken={transaction.buyToken} />

        <div className="flex flex-col items-center gap-1.5">
          {/* Announces the slow-trade copy; kept apart from the ticking timers. */}
          <div role="status" className="flex flex-col items-center gap-1.5">
            <div className={fontStyles({ variant: 'H5' })}>{title}</div>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: ['text-token-gray-800', 'text-center'],
              })}
            >
              {body}
            </div>
          </div>
          <div
            className={classNames(
              'w-full mt-6 rounded-lg border border-token-gray-300 p-4',
              'flex flex-col gap-3 text-token-gray-800',
            )}
          >
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: ['flex', 'items-center', 'justify-between', 'gap-4'],
              })}
            >
              <span>Estimated completion time</span>
              <span className="text-token-gray-1000">
                {formatCrossChainDuration(estimatedSeconds, true)}
              </span>
            </div>
            <div className="h-px bg-token-gray-300" />
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: ['flex', 'items-center', 'justify-between', 'gap-4'],
              })}
            >
              <span>Elapsed time</span>
              <span className="text-token-gray-1000">
                {formatCrossChainDuration(elapsedSeconds)}
              </span>
            </div>
          </div>
          <div
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['text-token-gray-700', 'text-center', 'pt-2'],
            })}
          >
            Leaving this state won&apos;t terminate the trade.
          </div>
        </div>
      </div>

      <div className="flex w-full gap-2.5 px-6 pb-4 desktop:pb-6">
        {details?.external && (
          <a
            href={details.href}
            target="_blank"
            rel="noopener noreferrer"
            className={DETAILS_BUTTON_CLASS_NAME}
          >
            {details.label}
          </a>
        )}
        {details?.external === false && (
          <Link href={details.href} className={DETAILS_BUTTON_CLASS_NAME}>
            {details.label}
          </Link>
        )}
        <button
          type="button"
          className={buttons({
            size: 'large',
            color: 'black',
            shape: 'pill',
            class: 'animate-[fadeIn_500ms_cubic-bezier(0.4,0,0,1)]',
          })}
          onClick={onDismiss}
        >
          New trade
        </button>
      </div>
    </div>
  );
};
